import axios from "axios"
import { store } from "../index"
import { apiError } from "../redux/error/actions"
import { getApiUrl } from "utils/networking"

axios.defaults.baseURL = getApiUrl()
axios.defaults.headers.common["tz"] = -new Date().getTimezoneOffset() / 60
axios.defaults.headers.common["X-Client-Timezone"] =
  Intl.DateTimeFormat().resolvedOptions().timeZone

// Queue for pending requests
let isAuthReady = false
const requestQueue: Array<() => void> = []

// Function to set auth ready state and process queue
export const setAuthReady = (ready: boolean) => {
  isAuthReady = ready
  if (ready) {
    processQueue()
  }
}

// Process queued requests
const processQueue = () => {
  while (requestQueue.length > 0) {
    const request = requestQueue.shift()
    if (request) {
      try {
        request()
      } catch (error) {
        console.error("Error processing queued request:", error)
      }
    }
  }
}

axios.interceptors.request.use(
  async (config) => {
    // Fix URL to ensure trailing slash
    if (config.url) {
      const [base, params] = config.url.split("?")
      if (!base.endsWith("/")) {
        config.url = [`${base}/`, params].join("?")
      }
    }

    // Add timezone data for POST requests
    if (config?.method === "post") {
      config.data = config.data ?? {}
      config.data.timezone ??= -new Date().getTimezoneOffset() / 60
    }

    // For requests that need authentication, ensure Auth is ready
    if (!isAuthReady && !config.url?.includes("auth/")) {
      // Return a promise that resolves when auth is ready
      return new Promise((resolve) => {
        requestQueue.push(() => resolve(config))
      })
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    // No response means a network error
    if (!error.response) {
      store.dispatch(apiError("Network error. Please check your connection."))
      return Promise.reject(error)
    }

    let message = ""
    const space = "\n\n"

    switch (error.response.status) {
      case 401:
        // if hit 401 we let auth0 sdk handle the session and redirect the user
        return Promise.reject(error)
      case 403:
        message = "Access Forbidden"
        break
      case 404:
        return Promise.reject(error.response)
      case 500:
      case 502:
      case 503:
      case 504:
        message =
          "We encountered an error. Please try again, and email support@getdashfuel.com if you continue to experience issues."
        break
      default: {
        message = "Error Occurred - "
        if (error.response.data?.detail) {
          message = error.response.data?.detail
        } else if (error.response.data?.startsWith?.("<!")) {
          message =
            "We encountered an error. Please try again, and email support@getdashfuel.com if you continue to experience issues."
        } else {
          console.error(error?.response?.data)
          Object.entries(error?.response?.data ?? {}).forEach(
            ([key, value]) =>
              (message +=
                key.replace("_", " ").toUpperCase() + ": " + value + space)
          )
        }
      }
    }

    // Suppress pop up if silent is pass as a query param
    if (error?.request?.responseURL?.includes("silent=")) {
      return Promise.reject(message)
    }

    if (
      error?.response?.data?.message
        ?.replaceAll(" ", "")
        ?.toLocaleLowerCase?.() ==
      error?.request?.responseURL?.split("interceptBypassMessage=").at(-1)
    ) {
      return Promise.resolve({ error: error?.response?.data })
    }

    try {
      const url = new URL(error?.request?.responseURL || "")
      const bypassCode = url?.searchParams?.get?.("interceptBypassCode")
      if (bypassCode === `${error.response.status}`) {
        return Promise.resolve(error)
      }
    } catch (e) {
      console.error("Invalid URL (interceptBypassCode)", e)
    }

    if (error.response.status !== 500 && message) {
      store.dispatch(apiError(message))
    } else if (
      message &&
      (window.location.host.startsWith("localhost") ||
        window.location.host.startsWith("dashfuel") ||
        error?.response?.data?.detail)
    ) {
      store.dispatch(apiError(message))
    }

    return Promise.reject(error)
  }
)
